function SettingAdminCustomProfile(props) {
  const channelType = ADMIN_CUSTOM_PROFILE_CHANNEL;
  const [customProfileItems, setCustomProfileItems] = React.useState(
    props.initialCustomProfileItems || [],
  );
  const [selectedRowKey, setSelectedRowKey] = React.useState("");
  const [draft, setDraft] = React.useState(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [uploadingChannel, setUploadingChannel] = React.useState("");
  const [cropperDialog, setCropperDialog] = React.useState({
    show: false,
    imageSrc: "",
    channelType: "",
  });
  const [resolvedImageUrlCache, setResolvedImageUrlCache] = React.useState({});
  const [imageResolveRetryToken, setImageResolveRetryToken] = React.useState(0);
  const imageResolveRetryRef = React.useRef({});
  const [appSettingState, setAppSettingState] = React.useState(
    props.appSetting || { is_enabled_custom_admin_name: 1, is_custom_admin_name_include_1on1: 0 }
  );

  React.useEffect(function () {
    if (props.appSetting) {
      setAppSettingState(props.appSetting);
    }
  }, [props.appSetting]);

  const rows = buildAdminCustomProfileRows(
    props.directoryInfo,
    props.businessData.current_user,
    customProfileItems,
  );
  const selectedRow = _.find(rows, { row_key: selectedRowKey }) || null;
  const selectedProfile = selectedRow
    ? getAdminCustomProfileItem(selectedRow.custom_profiles, channelType)
    : null;

  React.useEffect(
    function () {
      const imagePaths = _.uniq(
        _.compact(
          _.map(customProfileItems, function (item) {
            return _.get(item, "image_path");
          }),
        ),
      );
      const unresolvedImagePaths = _.filter(imagePaths, function (imagePath) {
        return !resolvedImageUrlCache[imagePath];
      });

      _.forEach(unresolvedImagePaths, function (imagePath) {
        resolveAndWarmImage(imagePath);
      });

      if (!unresolvedImagePaths.length) {
        return;
      }

      const shouldRetry = _.some(unresolvedImagePaths, function (imagePath) {
        return (imageResolveRetryRef.current[imagePath] || 0) < 5;
      });

      if (!shouldRetry) {
        return;
      }

      const retryTimer = setTimeout(function () {
        setImageResolveRetryToken(function (prev) {
          return prev + 1;
        });
      }, 1500);

      return function () {
        clearTimeout(retryTimer);
      };
    },
    [customProfileItems, resolvedImageUrlCache, imageResolveRetryToken],
  );

  React.useEffect(
    function () {
      if (!selectedRow) {
        if (selectedRowKey || draft) {
          setSelectedRowKey("");
          setDraft(null);
        }
        return;
      }

      if (selectedRowKey !== selectedRow.row_key || !draft) {
        hydrateDraftForRow(selectedRow);
        return;
      }

      setDraft(function (prev) {
        if (!prev || prev.row_key !== selectedRow.row_key) {
          return prev;
        }

        const currentChannel = _.get(prev.channels, channelType, {});
        if (currentChannel.dirty || currentChannel.pending_image_data) {
          return prev;
        }

        const nextDisplayName = _.get(selectedProfile, "display_name") || "";
        const nextImagePath = _.get(selectedProfile, "image_path") || "";
        const preservedPreviewUrl =
          currentChannel.image_path === nextImagePath
            ? currentChannel.preview_url || currentChannel.initial_preview_url || ""
            : "";
        const nextPreviewUrl =
          _.get(selectedProfile, "preview_url") ||
          resolvedImageUrlCache[nextImagePath] ||
          preservedPreviewUrl ||
          "";
        const initialChannel = _.get(prev.initialChannels, channelType, {});
        const hasSameInitialData =
          initialChannel.display_name === nextDisplayName &&
          initialChannel.image_path === nextImagePath;
        const hasSamePreview =
          currentChannel.preview_url === nextPreviewUrl &&
          currentChannel.initial_preview_url === nextPreviewUrl;

        if (hasSameInitialData && hasSamePreview) {
          return prev;
        }

        const updated = _.cloneDeep(prev);
        updated.channels[channelType].display_name = nextDisplayName;
        updated.channels[channelType].image_path = nextImagePath;
        updated.channels[channelType].preview_url = nextPreviewUrl;
        updated.channels[channelType].initial_preview_url = nextPreviewUrl;
        updated.initialChannels[channelType] = {
          display_name: nextDisplayName,
          image_path: nextImagePath,
        };
        return updated;
      });
    },
    [
      selectedRowKey,
      selectedRow ? selectedRow.row_key : "",
      _.get(selectedProfile, "display_name") || "",
      _.get(selectedProfile, "image_path") || "",
      _.get(selectedProfile, "preview_url") || "",
      resolvedImageUrlCache[_.get(selectedProfile, "image_path") || ""] || "",
    ],
  );

  function getChannelState(channelType) {
    if (!draft) {
      return {};
    }

    return _.get(draft.channels, channelType, {});
  }

  function resolveAndWarmImage(imagePath) {
    if (!imagePath) {
      return Promise.resolve("");
    }

    if (_.has(resolvedImageUrlCache, imagePath)) {
      delete imageResolveRetryRef.current[imagePath];
      return Promise.resolve(resolvedImageUrlCache[imagePath]);
    }

    let imageUrlPromise = Promise.resolve("");

    try {
      imageUrlPromise = props.resolveImageUrl
        ? props.resolveImageUrl(imagePath)
        : Promise.resolve("");
    } catch (error) {
      return Promise.resolve("");
    }

    return Promise.resolve(imageUrlPromise)
      .then(function (imageUrl) {
        if (!imageUrl) {
          imageResolveRetryRef.current[imagePath] =
            (imageResolveRetryRef.current[imagePath] || 0) + 1;
          return "";
        }

        return preloadAdminCustomProfileImage(imageUrl).then(function () {
          delete imageResolveRetryRef.current[imagePath];
          setResolvedImageUrlCache(function (prev) {
            if (prev[imagePath] === imageUrl) {
              return prev;
            }

            return _.assign({}, prev, {
              [imagePath]: imageUrl,
            });
          });

          return imageUrl;
        });
      })
      .catch(function () {
        imageResolveRetryRef.current[imagePath] =
          (imageResolveRetryRef.current[imagePath] || 0) + 1;
        return "";
      });
  }

  function hydrateDraftForRow(row) {
    const nextDraft = createAdminCustomProfileDraft(row);
    setSelectedRowKey(row.row_key);
    setDraft(nextDraft);
    setIsSaving(false);
    setUploadingChannel("");
    setCropperDialog({
      show: false,
      imageSrc: "",
      channelType: "",
    });

    const imagePath = _.get(nextDraft, ["channels", channelType, "image_path"]);
    const initialPreviewUrl = _.get(nextDraft, [
      "channels",
      channelType,
      "initial_preview_url",
    ]);
    if (!imagePath) {
      return;
    }

    if (initialPreviewUrl) {
      setResolvedImageUrlCache(function (prev) {
        if (prev[imagePath] === initialPreviewUrl) {
          return prev;
        }

        return _.assign({}, prev, {
          [imagePath]: initialPreviewUrl,
        });
      });
      return;
    }

    const cachedImageUrl = resolvedImageUrlCache[imagePath];
    if (cachedImageUrl) {
      setDraft(function (prev) {
        if (!prev || prev.row_key !== row.row_key) {
          return prev;
        }

        const updated = _.cloneDeep(prev);
        updated.channels[channelType].initial_preview_url = cachedImageUrl;
        if (!updated.channels[channelType].pending_image_data) {
          updated.channels[channelType].preview_url = cachedImageUrl;
        }
        return updated;
      });
      return;
    }

    resolveAndWarmImage(imagePath).then(
      function (imageUrl) {
        setDraft(function (prev) {
          if (!prev || prev.row_key !== row.row_key) {
            return prev;
          }

          if (!imageUrl) {
            return prev;
          }

          const updated = _.cloneDeep(prev);
          updated.channels[channelType].initial_preview_url = imageUrl;
          if (!updated.channels[channelType].pending_image_data) {
            updated.channels[channelType].preview_url = imageUrl;
          }
          return updated;
        });
      },
      function () {},
    );
  }

  function cancelEdit() {
    setSelectedRowKey("");
    setDraft(null);
    setIsSaving(false);
    setUploadingChannel("");
    setCropperDialog({
      show: false,
      imageSrc: "",
      channelType: "",
    });
  }

  function handleModeChange(newMode) {
    setDraft(function (prev) {
      if (!prev) { return prev; }
      const updated = _.cloneDeep(prev);
      updated.mode = newMode;
      updated.fieldErrors = {};
      return updated;
    });
  }

  function updateChannelValue(channelType, field, value) {
    setDraft(function (prev) {
      if (!prev) return prev;
      var ch = prev.channels[channelType] || {};
      return _.assign({}, prev, {
        channels: _.assign({}, prev.channels, {
          [channelType]: _.assign({}, ch, {
            [field]: value,
            dirty: true,
            save_error: "",
            errors: _.omit(ch.errors, field),
          }),
        }),
        fieldErrors: _.omit(prev.fieldErrors, channelType),
      });
    });
  }

  function clearChannelDraftValue(channelType) {
    setDraft(function (prev) {
      if (!prev) {
        return prev;
      }

      const updated = _.cloneDeep(prev);
      const initialChannel = _.get(updated.initialChannels, channelType, {});
      updated.channels[channelType].display_name = "";
      updated.channels[channelType].image_path = "";
      updated.channels[channelType].preview_url = "";
      updated.channels[channelType].initial_preview_url = "";
      updated.channels[channelType].pending_image_data = "";
      updated.channels[channelType].dirty =
        !!initialChannel.display_name || !!initialChannel.image_path;
      updated.channels[channelType].save_error = "";
      delete updated.channels[channelType].errors.display_name;
      delete updated.channels[channelType].errors.image_path;
      delete updated.fieldErrors[channelType];
      return updated;
    });
  }

  function clearChannelImage(channelType) {
    setDraft(function (prev) {
      if (!prev) {
        return prev;
      }

      const updated = _.cloneDeep(prev);
      const initialChannel = _.get(updated.initialChannels, channelType, {});
      const hasPendingImage = !!updated.channels[channelType].pending_image_data;

      if (hasPendingImage && initialChannel.image_path) {
        updated.channels[channelType].image_path = initialChannel.image_path;
        updated.channels[channelType].preview_url =
          updated.channels[channelType].initial_preview_url;
        updated.channels[channelType].pending_image_data = "";
        updated.channels[channelType].dirty =
          initialChannel.display_name !==
          updated.channels[channelType].display_name;
      } else {
        updated.channels[channelType].image_path = "";
        updated.channels[channelType].preview_url = "";
        updated.channels[channelType].pending_image_data = "";
        updated.channels[channelType].dirty =
          initialChannel.display_name !==
            updated.channels[channelType].display_name ||
          !!initialChannel.image_path;
      }

      updated.channels[channelType].save_error = "";
      delete updated.channels[channelType].errors.image_path;
      delete updated.fieldErrors[channelType];
      return updated;
    });
  }

  function getPendingSaveTargets(draftState) {
    const targets = [];
    const errors = {};

    if (!draftState.nexusUserData) {
      errors[channelType] =
        props.lang.__setting_admin_custom_profile_profile_id_missing;
      return { targets: targets, errors: errors };
    }

    const channel = _.get(draftState.channels, channelType, {});
    const initialChannel = _.get(draftState.initialChannels, channelType, {});
    const trimmedDisplayName = (channel.display_name || "").trim();
    const mode = draftState.mode || "default";
    const modeChanged = mode !== (draftState.initialMode || "default");
    const shouldSave =
      modeChanged ||
      channel.dirty ||
      !!channel.pending_image_data ||
      initialChannel.display_name !== channel.display_name ||
      initialChannel.image_path !== channel.image_path;

    if (shouldSave) {
      if (mode === "custom" && !trimmedDisplayName) {
        errors[channelType] =
          props.lang.__setting_admin_custom_profile_required_name;
      } else {
        const target = {
          mode: mode,
          display_name: mode === "custom" ? trimmedDisplayName : undefined,
          image_path: mode === "custom" ? channel.image_path : undefined,
          pending_image_data: mode === "custom" ? channel.pending_image_data : "",
          _channelType: channelType,
        };

        if (draftState.nexusUserData) {
          target.nexusUserData = draftState.nexusUserData;
        }

        targets.push(target);
      }
    }

    return { targets: targets, errors: errors };
  }

  function hasDraftChanges(draftState) {
    if (!draftState) {
      return false;
    }

    const channel = _.get(draftState.channels, channelType, {});
    const initialChannel = _.get(draftState.initialChannels, channelType, {});
    const modeChanged = (draftState.mode || "default") !== (draftState.initialMode || "default");

    return !!(
      modeChanged ||
      channel.dirty ||
      !!channel.pending_image_data ||
      initialChannel.display_name !== channel.display_name ||
      initialChannel.image_path !== channel.image_path
    );
  }

  function handleImageSelect(channelType, event) {
    const selectedFile = event.target.files && event.target.files[0];
    event.target.value = "";

    if (!selectedFile || !draft) {
      return;
    }

    if (!isAdminCustomProfileSupportedImageFile(selectedFile)) {
      props.toast.error(
        props.lang.__setting_admin_custom_profile_invalid_image_type ||
          "Please upload a PNG, JPG, or JPEG image.",
      );
      return;
    }

    const reader = new FileReader();
    reader.onload = function (loadEvent) {
      setCropperDialog({
        show: true,
        imageSrc: loadEvent.target.result,
        channelType: channelType,
      });
    };
    reader.readAsDataURL(selectedFile);
  }

  function handleCropCancel() {
    setCropperDialog({
      show: false,
      imageSrc: "",
      channelType: "",
    });
  }

  function handleCropConfirm(croppedImage) {
    const channelType = cropperDialog.channelType;

    if (!channelType) {
      return Promise.resolve();
    }

    setDraft(function (prev) {
      if (!prev) {
        return prev;
      }

      const updated = _.cloneDeep(prev);
      updated.channels[channelType].preview_url = croppedImage;
      updated.channels[channelType].pending_image_data = croppedImage;
      updated.channels[channelType].dirty = true;
      updated.channels[channelType].save_error = "";
      delete updated.channels[channelType].errors.image_path;
      delete updated.fieldErrors[channelType];
      return updated;
    });

    setCropperDialog({
      show: false,
      imageSrc: "",
      channelType: "",
    });

    return Promise.resolve();
  }

  function mergeSavedProfiles(savedProfiles) {
    setCustomProfileItems(function (prev) {
      const keyed = _.keyBy(prev, "nexus_user_id");

      _.forEach(savedProfiles, function (item) {
        if (!item.nexus_user_id) {
          return;
        }

        keyed[item.nexus_user_id] = _.assign(
          {},
          keyed[item.nexus_user_id],
          item,
        );
      });

      return _.values(keyed);
    });
  }

  function removeDeletedProfile(profileId, nexusUserId) {
    setCustomProfileItems(function (prev) {
      return _.filter(prev, function (item) {
        const matchesProfileId =
          profileId && _.get(item, "profile_id") === profileId;
        const matchesNexusUserId =
          nexusUserId && _.get(item, "nexus_user_id") === nexusUserId;

        return !(matchesProfileId || matchesNexusUserId);
      });
    });
  }

  function handleClearChannel(channelType) {
    if (!draft) {
      return;
    }
    clearChannelDraftValue(channelType);
  }

  function handleSave() {
    if (!draft) {
      return;
    }

    const pendingResult = getPendingSaveTargets(draft);
    if (!_.isEmpty(pendingResult.errors)) {
      setDraft(function (prev) {
        const updated = _.cloneDeep(prev);
        _.forEach(pendingResult.errors, function (message, channelType) {
          if (channelType === "global") {
            updated.globalError = message;
            return;
          }

          updated.fieldErrors[channelType] = message;
        });
        return updated;
      });
      return;
    }

    if (!pendingResult.targets.length) {
      cancelEdit();
      return;
    }

    setIsSaving(true);
    const target = pendingResult.targets[0];

    uploadPendingChannelImage(target)
      .then(function (uploadedTarget) {
        return props.saveProfile(uploadedTarget);
      })
      .then(function (savedProfile) {
        const savedProfileWithIdentity = _.assign({}, savedProfile, {
          nexus_user_id:
            savedProfile.nexus_user_id ||
            _.get(target, ["nexusUserData", "nexus_user_id"]) ||
            _.get(draft, ["nexus_user_id"]) ||
            "",
        });

        mergeSavedProfiles([savedProfileWithIdentity]);
        setDraft(function (prev) {
          if (!prev) {
            return prev;
          }

          const updated = _.cloneDeep(prev);
          const channelType = target._channelType;
          const channelDraft = updated.channels[channelType];

          updated.nexus_user_id =
            savedProfileWithIdentity.nexus_user_id || updated.nexus_user_id;
          updated.profile_id =
            savedProfileWithIdentity.profile_id || updated.profile_id;
          const savedMode = savedProfileWithIdentity.custom_admin_name_mode || target.mode || updated.mode || "default";
          updated.mode = savedMode;
          updated.initialMode = savedMode;
          channelDraft.display_name =
            savedProfileWithIdentity.display_name || "";
          channelDraft.image_path = savedProfileWithIdentity.image_path || "";
          channelDraft.preview_url =
            savedProfileWithIdentity.preview_url || channelDraft.preview_url;
          channelDraft.initial_preview_url =
            savedProfileWithIdentity.preview_url || channelDraft.initial_preview_url;
          channelDraft.pending_image_data = "";
          channelDraft.dirty = false;
          channelDraft.save_error = "";
          delete channelDraft.errors.display_name;
          delete channelDraft.errors.image_path;
          delete updated.fieldErrors[channelType];

          updated.initialChannels[channelType] = {
            display_name: channelDraft.display_name,
            image_path: channelDraft.image_path,
          };

          return updated;
        });
        setIsSaving(false);
        props.toast.success(
          props.lang.__setting_admin_custom_profile_save_successfully,
        );
      })
      .catch(function (error) {
        setDraft(function (prev) {
          const updated = _.cloneDeep(prev);
          updated.channels[target._channelType].save_error =
            getAdminCustomProfileMessage(error) ||
            (error.isProfileImageUploadError
              ? props.lang.__setting_admin_custom_profile_upload_unsuccessfully
              : props.lang.__setting_admin_custom_profile_save_unsuccessfully);
          return updated;
        });
        props.toast.error(
          getAdminCustomProfileMessage(error) ||
            (error.isProfileImageUploadError
              ? props.lang.__setting_admin_custom_profile_upload_unsuccessfully
              : props.lang.__setting_admin_custom_profile_save_unsuccessfully),
        );
        setIsSaving(false);
      });
  }

  function uploadPendingChannelImage(target) {
    if (!target.pending_image_data) {
      return Promise.resolve(target);
    }

    setUploadingChannel(target._channelType);
    return props
      .uploadProfileImage(
        target.pending_image_data,
        target.profile_id,
        target._channelType,
      )
      .then(function (uploadResult) {
        const updatedTarget = _.assign({}, target, {
          image_path: uploadResult.image_path,
          pending_image_data: "",
        });

        if (uploadResult.image_path && uploadResult.preview_url) {
          setResolvedImageUrlCache(function (prev) {
            if (prev[uploadResult.image_path] === uploadResult.preview_url) {
              return prev;
            }

            return _.assign({}, prev, {
              [uploadResult.image_path]: uploadResult.preview_url,
            });
          });
        }

        setDraft(function (prev) {
          if (!prev) {
            return prev;
          }

          const updated = _.cloneDeep(prev);
          updated.channels[target._channelType].image_path =
            uploadResult.image_path;
          updated.channels[target._channelType].preview_url =
            uploadResult.preview_url;
          updated.channels[target._channelType].initial_preview_url =
            uploadResult.preview_url;
          updated.channels[target._channelType].pending_image_data = "";
          return updated;
        });

        return updatedTarget;
      })
      .catch(function (error) {
        error.isProfileImageUploadError = true;
        throw error;
      })
      .finally(function () {
        setUploadingChannel("");
      });
  }

  function renderUserSummary(row, extraClassName) {
    return (
      <div
        className={
          "admin-custom-profile-user" +
          (extraClassName ? " " + extraClassName : "")
        }
      >
        <img
          className="admin-custom-profile-user-avatar"
          src={row.img_profile || ADMIN_CUSTOM_PROFILE_DEFAULT_IMAGE}
        />
        <div className="admin-custom-profile-user-text">
          <div className="admin-custom-profile-user-name">{row.displayname}</div>
          <div className="admin-custom-profile-user-email">{row.email}</div>
        </div>
      </div>
    );
  }

  function renderAccordionList() {
    return (
      <div className="admin-custom-profile-accordion">
        {_.map(rows, function (row) {
          const isSelected = selectedRow
            ? selectedRow.row_key === row.row_key
            : false;

          return (
            <div
              className={
                "acp-accordion-row" + (isSelected ? " -expanded" : "")
              }
              key={row.row_key || row.profile_id || row.email}
            >
              {/* ─── Header row ─── */}
              <div
                className="acp-accordion-header"
                onClick={function () {
                  if (isSelected) {
                    cancelEdit();
                  } else {
                    hydrateDraftForRow(row);
                  }
                }}
              >
                {renderUserSummary(row, "acp-accordion-user")}
                <div className="acp-accordion-badges">
                  <AdminCustomProfileStatusBadge
                    customProfiles={row.custom_profiles}
                    channelType={channelType}
                    lang={props.lang}
                  />
                </div>
                {!isSelected ? (
                  <button
                    type="button"
                    className="acp-accordion-edit-btn"
                    onClick={function (e) {
                      e.stopPropagation();
                      hydrateDraftForRow(row);
                    }}
                  >
                    <i className="icon icon-hover icon-pencil"></i>
                  </button>
                ) : null}
              </div>

              {/* ─── Expanded editor ─── */}
              {isSelected && draft ? (
                <div className="acp-accordion-body">
                  <AdminCustomProfileInlineEditor
                    draft={draft}
                    channelType={channelType}
                    lang={props.lang}
                    isSaving={isSaving}
                    uploadingChannel={uploadingChannel}
                    cropperDialog={cropperDialog}
                    resolvedImageUrlCache={resolvedImageUrlCache}
                    onModeChange={handleModeChange}
                    onUpdateChannelValue={updateChannelValue}
                    onClearChannelImage={clearChannelImage}
                    onImageSelect={handleImageSelect}
                  />
                  {(() => {
                    const selectedChannelState = getChannelState(channelType);
                    const canSaveChannel = hasRequiredDisplayName(
                      selectedChannelState,
                    );
                    const currentDraftMode = draft.mode || "default";
                    const isSaveDisabled =
                      isSaving ||
                      uploadingChannel ||
                      cropperDialog.show ||
                      !hasDraftChanges(draft) ||
                      (currentDraftMode === "custom" && !canSaveChannel);

                    return (
                      <div className="acp-accordion-footer">
                        <div className="acp-accordion-footer-left"></div>
                        <div className="acp-accordion-footer-right">
                          <button
                            type="button"
                            className="sp-btn -link"
                            onClick={cancelEdit}
                            disabled={isSaving}
                          >
                            {props.lang.__cancel}
                          </button>
                          <button
                            type="button"
                            className="sp-btn -blue"
                            onClick={handleSave}
                            disabled={isSaveDisabled}
                          >
                            {isSaving ? (
                              <i className="icon icon-circle-notch animate-spin"></i>
                            ) : null}
                            {isSaving ? " " : ""}
                            {props.lang.__save}
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className={
        "admin-custom-profile-page" + (props.embedded ? " -embedded" : "")
      }
    >
      {!props.embedded ? (
        <div className="admin-custom-profile-header">
          <div className="sp-label">{props.lang.__setting_admin_custom_profile}</div>
          <div className="sp-text-help">
            {props.lang.__setting_admin_custom_profile_description}
          </div>
        </div>
      ) : null}
      {props.appSetting ? (
        <div className="acp-app-setting-row -top">
          <div className="acp-app-setting-label-group">
            <span className="acp-app-setting-label">
              {props.lang.__setting_admin_custom_profile_enabled || "เปิดใช้งาน"}
            </span>
            {props.lang.__setting_admin_custom_profile_group_only_hint ? (
              <div className="sp-text-help">
                {props.lang.__setting_admin_custom_profile_group_only_hint}
              </div>
            ) : null}
          </div>
          <div className={"sp-toggle" + (!appSettingState.is_enabled_custom_admin_name ? " -off" : "")}>
            <label>
              <input
                type="checkbox"
                checked={!!appSettingState.is_enabled_custom_admin_name}
                onChange={function (e) {
                  const next = _.assign({}, appSettingState, {
                    is_enabled_custom_admin_name: e.target.checked ? 1 : 0,
                  });
                  setAppSettingState(next);
                  var promise = props.updateAppSetting && props.updateAppSetting(next);
                  if (promise && promise.then) {
                    promise.then(function () {
                      props.toast && props.toast.success(
                        props.lang.__setting_admin_custom_profile_app_save_success || "บันทึกการตั้งค่าสำเร็จ"
                      );
                    });
                  }
                }}
              />
              <div className="toggle-button cursor-pointer"><span></span></div>
            </label>
          </div>
        </div>
      ) : null}
      {props.appSetting && appSettingState.is_enabled_custom_admin_name ? (
        <React.Fragment>
        <div className="admin-custom-profile-card">
          <div className="admin-custom-profile-accordion-wrap">
            {renderAccordionList()}
          </div>
        </div>
        </React.Fragment>
      ) : (
        !props.appSetting ? (
          <div className="admin-custom-profile-accordion-wrap">
            {renderAccordionList()}
          </div>
        ) : null
      )}
      <AdminCustomProfileCropperDialog
        show={cropperDialog.show}
        imageSrc={cropperDialog.imageSrc}
        lang={props.lang}
        toast={props.toast}
        onCancel={handleCropCancel}
        onConfirm={handleCropConfirm}
      />
    </div>
  );
}
